import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReviewAnswers, type ReviewSnapshot } from '@snaphire/contracts';
import { getObject, command, type Row } from './api/client';
import { useAccess } from './api/data';
import { useSession } from './App';
import { Select, Button, Empty, ErrorState, Field, Loading, Status, useToast } from './ui';
import { statusLabel } from './i18n';
import { ActionForm } from './forms';
import { useNavigate } from '@tanstack/react-router';

type Submission = Row & {
  reviewerAccountId: string;
  reviewerType: 'SELF' | 'LINE_MANAGER';
  status: 'DRAFT' | 'SUBMITTED';
  content: ReviewAnswers;
};
type Review = Row & {
  snapshot: ReviewSnapshot;
  status: string;
  reviewDueAt: string;
  submissions: Submission[];
  coverage: number;
  finalGrade: number | null;
  recommendedGrade: number | null;
};
type Question = {
  key: string;
  title: string;
  description: string;
  type: 'NUMBER' | 'MASTERY' | 'TEXT' | 'CHOICE';
  required: boolean;
  options?: string[];
  group: string;
};
export function reviewQuestions(
  snapshot: ReviewSnapshot,
  type: 'SELF' | 'LINE_MANAGER',
): Question[] {
  return [
    ...snapshot.tasks
      .filter((task) => task.status !== 'EXCLUDED')
      .flatMap((task) =>
        task.definition.criteria.map((criterion) => ({
          key: `task:${task.id}:${criterion.key}`,
          title: criterion.title,
          description: criterion.description,
          type: 'NUMBER' as const,
          required: criterion.required,
          group: task.definition.title,
        })),
      ),
    ...snapshot.profile.skills.map((skill) => ({
      key: `skill:${skill.skillId}`,
      title: skill.name,
      description: skill.content.description,
      type: 'MASTERY' as const,
      required: skill.required,
      group: 'Навыки',
    })),
    ...snapshot.profile.evaluationTemplate.values.map((value) => ({
      key: `value:${value.key}`,
      title: value.title,
      description: value.description,
      type: 'NUMBER' as const,
      required: value.required,
      group: 'Ценности',
    })),
    ...snapshot.profile.evaluationTemplate.questionGroups
      .filter((group) => group.reviewerTypes.includes(type))
      .flatMap((group) =>
        group.questions.map((question) => ({
          key: `question:${question.key}`,
          title: question.title,
          description: question.description,
          type: question.type === 'SCALE' ? ('NUMBER' as const) : question.type,
          required: question.required,
          options: question.options,
          group: group.title,
        })),
      ),
  ];
}
export function ReviewSubmissionLinks({ review }: { review: Row }) {
  const submissions = (review.submissions || []) as Submission[];
  return (
    <section className="detail-section">
      <h3>Формы оценки</h3>
      {!submissions.length && <p className="muted">Формы появятся после открытия оценки.</p>}
      <div className="assessment-links">
        {submissions.map((submission) => {
          const type = submission.reviewerType;
          return (
            <Link
              className="assessment-link"
              key={type}
              to="/performance/reviews/$reviewId/submission/$type"
              params={{ reviewId: review.id, type }}
            >
              <div>
                <strong>{type === 'SELF' ? 'Самооценка' : 'Оценка руководителя'}</strong>
                <Status value={submission.status} />
              </div>
              <span className="muted">Открыть форму</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
export function ReviewSubmissionPage() {
  const path = useRouterState({ select: (state) => state.location.pathname });
  const parts = path.split('/');
  const id = parts[3] || '';
  const type = parts[5] === 'SELF' ? 'SELF' : 'LINE_MANAGER';
  const review = useQuery({
    queryKey: ['review-form-parent', id],
    queryFn: ({ signal }) => getObject<Review>('/api/v1/reviews/{id}', { id }, signal),
  });
  const submission = useQuery({
    queryKey: ['review-submission', id, type],
    queryFn: ({ signal }) =>
      getObject<Submission>('/api/v1/reviews/{id}/submissions/{type}', { id, type }, signal),
  });
  if (review.isLoading || submission.isLoading) return <Loading />;
  if (review.error || submission.error)
    return <ErrorState error={review.error || submission.error} />;
  if (!review.data || !submission.data) return <Empty title="Форма не найдена" />;
  return (
    <AssessmentForm
      key={`${submission.data.id}:${submission.data.version}`}
      review={review.data}
      submission={submission.data}
      type={type}
    />
  );
}
export function ReviewReturnPage() {
  const parts = useRouterState({ select: (state) => state.location.pathname }).split('/');
  const id = parts[3] || '';
  const type = parts[5] === 'SELF' ? 'SELF' : 'LINE_MANAGER';
  const navigate = useNavigate();
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['review-submission', id, type],
    queryFn: ({ signal }) =>
      getObject<Submission>('/api/v1/reviews/{id}/submissions/{type}', { id, type }, signal),
  });
  const back = () =>
    void navigate({
      to: '/performance/reviews/$reviewId/submission/$type',
      params: { reviewId: id, type },
    });
  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <ErrorState error={query.error} />;
  return (
    <ActionForm
      action={{
        label: 'Вернуть оценку на доработку',
        path: '/api/v1/reviews/{id}/submissions/{type}/return',
        params: { id, type },
        initial: { version: query.data.version },
        permission: 'performance_reviews.manage',
      }}
      onClose={back}
      onSuccess={() => {
        void client.invalidateQueries();
        back();
      }}
    />
  );
}
function AssessmentForm({
  review,
  submission,
  type,
}: {
  review: Review;
  submission: Submission;
  type: 'SELF' | 'LINE_MANAGER';
}) {
  const session = useSession();
  const access = useAccess();
  const client = useQueryClient();
  const toast = useToast();
  const [content, setContent] = useState<ReviewAnswers>(submission.content);
  const [version, setVersion] = useState(submission.version!);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const questions = reviewQuestions(review.snapshot, type);
  const groups = [...new Set(questions.map((question) => question.group))];
  const own = submission.reviewerAccountId === session.account?.id;
  const editable =
    own &&
    access.can('performance_reviews.write_own') &&
    review.status === 'IN_PROGRESS' &&
    submission.status === 'DRAFT' &&
    new Date(review.reviewDueAt) > new Date();
  const answer = (key: string) =>
    content.answers.find((item) => item.key === key) || { key, value: null, comment: '' };
  const change = (key: string, part: Partial<ReviewAnswers['answers'][number]>) =>
    setContent((previous) => ({
      ...previous,
      answers: [
        ...previous.answers.filter((item) => item.key !== key),
        { ...answer(key), ...part },
      ],
    }));
  const save = async (submit: boolean) => {
    setPending(true);
    setError(null);
    try {
      if (
        submit &&
        questions.some(
          (question) =>
            question.required &&
            (answer(question.key).value === null || answer(question.key).value === ''),
        )
      )
        throw new Error('Заполните обязательные оценки и вопросы');
      const result = await command(
        'PATCH',
        '/api/v1/reviews/{id}/submissions/{type}',
        { version, content },
        { id: review.id, type },
      );
      setVersion(Number(result.version));
      if (submit)
        await command(
          'POST',
          '/api/v1/reviews/{id}/submissions/{type}/submit',
          { version: result.version },
          { id: review.id, type },
        );
      toast(submit ? 'Оценка отправлена' : 'Черновик сохранён');
      await client.invalidateQueries();
    } catch (cause) {
      setError(cause as Error);
    } finally {
      setPending(false);
    }
  };
  const grades = review.snapshot.profile.evaluationTemplate.grades;
  return (
    <section className="form-page assessment-page">
      <div className="form-page-heading">
        <h1>{type === 'SELF' ? 'Самооценка' : 'Оценка руководителя'}</h1>
      </div>
      <div className="assessment-summary">
        <Status value={submission.status} />
        <span className="muted">
          Срок: {new Date(review.reviewDueAt).toLocaleDateString('ru-RU')}
        </span>
      </div>
      <form
        className="domain-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save(true);
        }}
      >
        {groups.map((group) => (
          <section className="panel assessment-group" key={group}>
            <h2>{group}</h2>
            {questions
              .filter((question) => question.group === group)
              .map((question) => (
                <div className="assessment-question" key={question.key}>
                  <h3>
                    {question.title}
                    {question.required && <span aria-label="Обязательно"> *</span>}
                  </h3>
                  {question.description && <p className="muted">{question.description}</p>}
                  {question.type === 'NUMBER' ? (
                    <div className="rating-scale" role="radiogroup" aria-label={question.title}>
                      {[1, 2, 3, 4, 5].map((grade) => (
                        <button
                          key={grade}
                          type="button"
                          role="radio"
                          aria-checked={answer(question.key).value === grade}
                          className={answer(question.key).value === grade ? 'selected' : ''}
                          disabled={!editable || pending}
                          onClick={() => change(question.key, { value: grade })}
                        >
                          <strong>{grade}</strong>
                          <span>
                            {grades.find((item) => item.grade === grade)?.title || String(grade)}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : question.type === 'TEXT' ? (
                    <Field label="Ответ">
                      <textarea
                        name={question.key}
                        value={String(answer(question.key).value || '')}
                        disabled={!editable || pending}
                        onChange={(event) => change(question.key, { value: event.target.value })}
                      />
                    </Field>
                  ) : (
                    <Field label={question.type === 'MASTERY' ? 'Уровень владения' : 'Ответ'}>
                      <Select
                        name={question.key}
                        value={String(answer(question.key).value || '')}
                        disabled={!editable || pending}
                        onChange={(event) => change(question.key, { value: event.target.value })}
                      >
                        <option value="">Выберите значение</option>
                        {(question.type === 'MASTERY'
                          ? ['POOR', 'BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT']
                          : question.options || []
                        ).map((value) => (
                          <option key={value} value={value}>
                            {question.type === 'MASTERY' ? statusLabel(value) : value}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  )}
                  <Field label="Комментарий">
                    <textarea
                      name={`${question.key}-comment`}
                      value={answer(question.key).comment}
                      disabled={!editable || pending}
                      onChange={(event) => change(question.key, { comment: event.target.value })}
                    />
                  </Field>
                </div>
              ))}
          </section>
        ))}
        <section className="panel assessment-group">
          <h2>Обратная связь</h2>
          <Field label="Сильные стороны">
            <textarea
              name="strengths"
              disabled={!editable || pending}
              value={content.strengths}
              onChange={(event) =>
                setContent((previous) => ({ ...previous, strengths: event.target.value }))
              }
            />
          </Field>
          <Field label="Зоны развития">
            <textarea
              name="development"
              disabled={!editable || pending}
              value={content.development}
              onChange={(event) =>
                setContent((previous) => ({ ...previous, development: event.target.value }))
              }
            />
          </Field>
        </section>
        {error && <ErrorState error={error} />}
        <footer>
          {editable ? (
            <>
              <Button onClick={() => void save(false)} disabled={pending}>
                Сохранить черновик
              </Button>
              <Button type="submit" className="primary" disabled={pending}>
                {pending ? 'Сохранение…' : 'Отправить оценку'}
              </Button>
            </>
          ) : (
            <Link
              className="pill"
              to="/$section/$resource/$entityId"
              params={{ section: 'performance', resource: 'reviews', entityId: review.id }}
            >
              К оценке
            </Link>
          )}
        </footer>
      </form>
      {submission.status === 'SUBMITTED' &&
        access.can('performance_reviews.manage') &&
        ['IN_PROGRESS', 'READY'].includes(review.status) && (
          <Link
            className="pill"
            to="/performance/reviews/$reviewId/submission/$type/return"
            params={{ reviewId: review.id, type }}
          >
            Вернуть на доработку
          </Link>
        )}
    </section>
  );
}
